import { useCssVar } from '@vueuse/core';
import { registerTheme } from 'echarts/core';
import { color } from 'echarts/core';
import { computed, provide, unref, watch } from 'vue';
import { THEME_KEY } from 'vue-echarts';
import { useI18n } from 'vue-i18n';
import { convert, resolve } from '@asamuzakjp/css-color';
import type { XAXisOption } from 'echarts/types/dist/shared';

export type DecalType = 'dashed' | 'dotted';

const useChartColorVariable = (name: string, opacity: number = 100, shadeColor = 'transparent') => {
  const colorVar = useCssVar(name);
  const shadedColorVar = shadeColor.startsWith('--')
    ? useCssVar(shadeColor)
    : computed(() => shadeColor);

  return computed(() => {
    const base = unref(colorVar);
    const shade = unref(shadedColorVar);

    if (!base) return undefined;
    if (shade && opacity < 100) {
      return convert.colorToHex(
        resolve(`color-mix(in oklab, ${base} ${opacity}%, ${shade})`) ?? base,
        {
          alpha: true,
        },
      );
    } else {
      return convert.colorToHex(base, { alpha: true });
    }
  });
};

export const useChartTheme = (root?: boolean) => {
  const { t } = useI18n();
  const colorBase100 = useChartColorVariable('--color-base-100');
  const colorBase300 = useChartColorVariable('--color-base-300');
  const colorBaseContent = useChartColorVariable('--color-base-content');
  const colorBaseContentMuted = useChartColorVariable(
    '--color-base-content',
    60,
    '--color-base-200',
  );
  const colorBorder = useChartColorVariable('--color-base-content', 30, '--color-base-200');
  const colorPrimary = useChartColorVariable('--color-primary');
  const colorSecondary = useChartColorVariable('--color-secondary');
  const colorSuccess = useChartColorVariable('--color-success');
  const colorError = useChartColorVariable('--color-error');

  const colors = computed(() => [
    colorPrimary.value,
    colorSecondary.value,
    colorSuccess.value,
    colorError.value,
    '#A57BA7',
    '#BCC3CD',
    '#BE8041',
  ]);

  const axisColors = computed<
    Pick<XAXisOption, 'axisLine' | 'axisTick' | 'axisLabel' | 'splitLine' | 'minorSplitLine'>
  >(() => ({
    axisLine: {
      lineStyle: { color: colorBaseContentMuted.value ?? undefined },
    },
    splitLine: {
      lineStyle: { color: colorBorder.value ?? undefined },
    },
    axisTick: {
      lineStyle: { color: colorBorder.value ?? undefined },
    },
    axisLabel: {
      color: colorBaseContent.value ?? undefined,
    },
  }));

  const decals: Record<DecalType, any> = {
    dashed: {
      color: 'rgba(0, 0, 0, 0.2)',
      dashArrayX: [1, 0],
      dashArrayY: [2, 5],
      symbolSize: 2,
      rotation: Math.PI / 6,
    },
    dotted: {
      color: 'rgba(0, 0, 0, 0.2)',
      dashArrayX: [1, 1],
      dashArrayY: [1, 1],
      symbolSize: 2,
      rotation: Math.PI / 6,
    },
  };

  const theme = computed(() => ({
    color: colors.value,
    backgroundColor: 'transparent',
    textStyle: { color: colorBaseContent.value },
    title: {
      textStyle: { color: colorBaseContent.value },
      subtextStyle: { color: colorBaseContent.value },
    },
    tooltip: {
      backgroundColor: 'var(--color-base-300)',
      borderWidth: 0,
      borderColor: 'transparent',
      textStyle: { color: 'var(--color-base-content)' },
    },
    legend: {
      textStyle: { color: colorBaseContent.value },
      type: 'scroll',
      orient: 'horizontal',
      bottom: 'bottom',
      left: 'center',
      pageIconColor: colorBaseContent.value,
      pageIconInactiveColor: colorBorder.value,
      pageTextStyle: { color: colorBaseContent.value },
    },
    grid: {
      top: 5,
      left: 40,
      right: 40,
      bottom: 60,
    },
    xAxis: axisColors.value,
    yAxis: axisColors.value,
    radiusAxis: axisColors.value,
    angleAxis: axisColors.value,
    bar: {
      emphasis: { focus: 'series' },
    },
    pie: {
      center: ['50%', '44%'],
      radius: '75%',
      itemStyle: { borderWidth: 0, borderColor: 'transparent' },
      label: { formatter: '{d}%', color: colorBaseContent.value },
      labelLine: { length: 10, length2: 10 },
      emphasis: { focus: 'data' },
    },
    visualMap: {
      textStyle: { color: colorBaseContent.value },
      type: 'piecewise',
      orient: 'horizontal',
      left: 'center',
      formatter: (a: number, b: number) => `${Math.floor(a)} - ${Math.floor(b)}`,
      inRange: {
        color: [color.modifyAlpha(colorPrimary.value ?? '#000000', 0.2), colorPrimary.value],
      },
      outOfRange: {
        color: colorBase300.value,
      },
    },
    heatmap: {
      itemStyle: { borderRadius: 3, borderColor: colorBase100.value, borderWidth: 2 },
    },
    calendar: {
      top: 25,
      left: 50,
      right: 10,
      bottom: 45,
      cellSize: ['auto', 'auto'],
      textStyle: { color: colorBaseContent.value },
      itemStyle: { borderColor: 'transparent', color: 'transparent' },
      splitLine: { lineStyle: { color: colorBorder.value } },
      monthLabel: { color: colorBaseContent.value },
      dayLabel: {
        color: colorBaseContent.value,
        show: true,
        firstDay: 1,
        nameMap: Array.from({ length: 7 }, (_, i) => t(`stats.chart.weekday-short.${i}`)),
      },
      yearLabel: { show: false },
    },
    radar: {
      center: ['50%', '44%'],
      radius: '65%',
      axisName: { color: colorBaseContent.value },
      splitLine: { lineStyle: { color: colorBorder.value } },
      splitArea: { areaStyle: { color: [colorBase100.value, colorBase300.value] } },
    },
    polar: {
      center: ['50%', '44%'],
      radius: '65%',
    },
    geo: {
      center: ['60%', '50%'],
    },
  }));

  if (root) {
    watch(theme, (defaultTheme) => registerTheme('default', defaultTheme), { immediate: true });

    provide(THEME_KEY, 'default');
  }

  return {
    colors,
    decals,
    axisColors,
  };
};
